import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  CheckCircleOutline as SignedIcon,
  AccessTime as PendingIcon,
} from '@mui/icons-material';
import { portalApi, ServiceAgreementData } from '../services/portalApi';
import { useTranslation } from 'react-i18next';
import { i18n } from '../i18n';

export const ServiceAgreement: React.FC = () => {
  const { t } = useTranslation();
  const [agreement, setAgreement] = useState<ServiceAgreementData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    portalApi
      .getServiceAgreement()
      .then((data) => {
        setAgreement(data);
      })
      .catch((err) => {
        setError(err.message || t('Failed to load agreement'));
      })
      .finally(() => setLoading(false));
  }, [t]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{error}</Typography>
      </Box>
    );
  }

  if (!agreement || !agreement.status) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {t('No service agreement on file.')}
        </Typography>
      </Box>
    );
  }

  const isSigned = agreement.status === 'signed';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ flexGrow: 1 }}>
          {t('Service Agreement')}
        </Typography>
        {isSigned ? (
          <Chip
            icon={<SignedIcon />}
            label={t('Signed')}
            color="success"
            variant="outlined"
          />
        ) : (
          <Chip
            icon={<PendingIcon />}
            label={t('Pending Signature')}
            color="warning"
            variant="outlined"
          />
        )}
      </Box>

      <Paper sx={{ p: { xs: 3, sm: 4 } }}>
        {agreement.customerName && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('Prepared for')}
            </Typography>
            <Typography fontWeight="medium" sx={{ mb: 3 }}>
              {agreement.customerName}
            </Typography>
          </>
        )}

        <Box
          sx={{
            p: 3,
            bgcolor: '#fafafa',
            borderRadius: 1,
            border: '1px solid #e0e0e0',
            whiteSpace: 'pre-wrap',
            lineHeight: 1.7,
            fontSize: '0.95rem',
          }}
        >
          {agreement.agreementBody}
        </Box>

        {isSigned && (
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4, flexWrap: 'wrap' }}>
              {agreement.signatureUrl && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('Signature')}
                  </Typography>
                  <Box
                    component="img"
                    src={agreement.signatureUrl}
                    alt={t('Signature')}
                    sx={{
                      maxWidth: 300,
                      maxHeight: 100,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      bgcolor: 'white',
                    }}
                  />
                </Box>
              )}
              {agreement.signedAt && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('Date signed')}
                  </Typography>
                  <Typography fontWeight="medium">
                    {new Date(agreement.signedAt).toLocaleDateString(i18n.resolvedLanguage === 'es' ? 'es-MX' : 'en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
